import { ErrorOutline as ErrorOutlineIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { forwardRef, memo, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ImageDraggable from 'src/components/image/image-draggable';
import TextIconLabel from 'src/components/TextIconLabel';
import { NewPostStyledComponent } from 'src/pages/new-post-new/newPostStyled.component';
import { CommonFunction } from 'src/utils/commonFunctions/common-function.component';
import { CommonHelper } from 'src/utils/commonHelper';
import { NewPostErrorUtil } from '../new-post-errors-util';
import PostDescription from '../post-description.component';
import { PostUtil } from '../post-util';

const SnapchatConditionComponent = forwardRef(({ updateRequestData, isOrgConnectedProfilesData }, ref) => {
  const { t: translate } = useTranslation();
  const theme = useTheme();

  // State Variables
  const [socialMediaRequestData, setSocialMediaRequestData] = useState([]);
  const [requestData, setRequestData] = useState([]);
  const [manualExpand, setManualExpand] = useState({});

  useEffect(() => {
    checkForErrors();
  }, []);

  useImperativeHandle(ref, () => ({
    checkForErrors,
    handleRequestData,
  }));

  useEffect(() => {
    Object.keys(requestData).forEach((profileId) => {
      const hasErrors = requestData[profileId].some((profile) => profile.errors?.length > 0);
      if (!hasErrors && manualExpand[profileId] === undefined) {
        setManualExpand((prev) => ({ ...prev, [profileId]: true }));
      }
    });
  }, [requestData]);

  const handleRequestData = (data) => {
    checkForErrors(data, false);
  };

  const updateRequestDataToParent = (data) => {
    updateRequestData(data, PostUtil.ServiceName.SNAPCHAT);
  };

  const handleChangeSocialMediaRequestData = (data, isUpdateDataToParent = true) => {
    const formattedRequestData = CommonHelper.B2SGroupBy(data, 'connected_profile_id');
    setRequestData({ ...formattedRequestData });
    setSocialMediaRequestData([...data]);
    if (isUpdateDataToParent) {
      updateRequestDataToParent(data);
    }
  };

  const checkForErrors = (data = null, isUpdateDataToParent = true) => {
    let sRequestData = data ? [...data] : [...socialMediaRequestData];
    if (sRequestData?.length > 0) {
      sRequestData.forEach((request) => {
        request['errors'] = getSnapchatErrors(request);
      });
      handleChangeSocialMediaRequestData(sRequestData, isUpdateDataToParent);
    } else {
      handleChangeSocialMediaRequestData([]);
    }
  };

  const handleReOrderFiles = (profileId, postType, oldIndex, newIndex) => {
    let sRequestData = [...socialMediaRequestData];
    let reqData = sRequestData.find((a) => a.connected_profile_id === parseInt(profileId) && a.post_type === postType);
    if (!reqData) return;

    let files = [...reqData.media_data];
    let mediaFiles = CommonHelper.ReArrangeArray(files, oldIndex, newIndex);
    reqData.media_url = mediaFiles.map((a) => a.media_url).join(',');
    reqData.media_data = mediaFiles;
    checkForErrors(sRequestData);
  };

  const handleRemoveFiles = (profileId, postType, mediaUrl) => {
    let sRequestData = [...socialMediaRequestData];
    let rData = sRequestData.find((a) => a.connected_profile_id === parseInt(profileId) && a.post_type === postType);
    if (!rData) return;

    let files = [...rData.media_data];
    let index = files.findIndex((a) => a.media_url === mediaUrl);
    if (index >= 0) files.splice(index, 1);

    rData.media_url = files.map((a) => a.media_url).join(',');
    rData.media_data = files;
    checkForErrors(sRequestData);
  };

  const handleChangeDescription = (profileId, profile, value) => {
    let sRequestData = [...socialMediaRequestData];
    let connectedProfile = sRequestData.find(
      (a) => a.connected_profile_id === parseInt(profileId) && a.post_type === profile.post_type
    );
    if (connectedProfile) {
      connectedProfile.description = value;
      checkForErrors(sRequestData);
    }
  };

  const handleChangeTitle = (profileId, profile, value) => {
    let sRequestData = [...socialMediaRequestData];
    let connectedProfile = sRequestData.find(
      (a) => a.connected_profile_id === parseInt(profileId) && a.post_type === profile.post_type
    );
    if (connectedProfile) {
      connectedProfile.title = value;
      checkForErrors(sRequestData);
    }
  };

  const handleSaveToProfile = (profileId, profile, value) => {
    let sRequestData = [...socialMediaRequestData];
    let connectedProfile = sRequestData.find(
      (a) => a.connected_profile_id === parseInt(profileId) && a.post_type === profile.post_type
    );
    if (connectedProfile) {
      connectedProfile.save_to_profile = value;
      checkForErrors(sRequestData);
    }
  };

  const getSnapchatErrors = (request) => {
    let postType = request.post_type;
    if (postType === PostUtil.PostType.PUBLIC_STORY) {
      return NewPostErrorUtil.SnapChatPublicStoryConditions(request, translate);
    } else if (postType === PostUtil.PostType.SAVED_STORY) {
      return NewPostErrorUtil.SnapChatSavedStoryConditions(request, translate);
    } else if (postType === PostUtil.PostType.SPOTLIGHT) {
      return NewPostErrorUtil.SnapChatSpotlightConditions(request, translate);
    }
    return ['Invalid Post Type'];
  };

  const handleAccordion = (profileId, hasErrors) => {
    if (hasErrors) return;

    setManualExpand((prev) => ({
      ...prev,
      [profileId]: !prev[profileId],
    }));

    let sRequestData = [...socialMediaRequestData];
    let reqDatas = sRequestData.filter((a) => a.connected_profile_id === parseInt(profileId));
    reqDatas.forEach((data) => {
      data['isExpanded'] = !data['isExpanded'];
    });

    handleChangeSocialMediaRequestData(sRequestData, false);
  };

  const checkPostTypeChecked = (profileId, postType) => {
    return requestData[profileId]?.some((a) => a.post_type === postType) || false;
  };

  const errorView = (request) => (
    <Box style={{ marginTop: '0.5rem' }}>
      <Typography sx={{ fontSize: 16, fontWeight: 800 }}>
        {translate('post_conditions.error_description', 'Error Description')}
      </Typography>
      {request.errors.map((errorMessage, idx) => (
        <TextIconLabel
          key={idx}
          icon={<ErrorOutlineIcon sx={{ color: theme.palette.error.main, mr: 1, fontSize: 18 }} />}
          value={errorMessage}
          sx={{ typography: 'caption', color: theme.palette.error.main, mb: 1, fontWeight: 800, fontSize: 14 }}
        />
      ))}
    </Box>
  );

  const POST_TYPES = {
    PUBLIC_STORY: { value: PostUtil.PostType.PUBLIC_STORY, label: translate('preview.public_story', 'Public Story') },
    SAVED_STORY: { value: PostUtil.PostType.SAVED_STORY, label: translate('preview.saved_story', 'Saved Story') },
    SPOTLIGHT: { value: PostUtil.PostType.SPOTLIGHT, label: translate('preview.spotlight', 'Spotlight') },
  };

  const handlePostChange = (profileId, postType) => {
    let sRequestData = [...socialMediaRequestData];
    let profileCount = sRequestData.filter((a) => a.connected_profile_id === parseInt(profileId)).length;
    let index = sRequestData.findIndex(
      (a) => a.connected_profile_id === parseInt(profileId) && a.post_type === postType
    );

    if (index >= 0) {
      if (profileCount > 1) sRequestData.splice(index, 1);
    } else {
      let sNetwork = { ...sRequestData.find((a) => a.connected_profile_id === parseInt(profileId)) };
      sNetwork.post_type = postType;
      sNetwork.errors = getSnapchatErrors(sNetwork);
      sRequestData.push(sNetwork);
    }
    checkForErrors(sRequestData);
  };

  return (
    <>
      {Object.keys(requestData).length > 0 &&
        Object.keys(requestData).map((profileId) => {
          const hasErrors = requestData[profileId].some((profile) => profile.errors?.length > 0);
          const borderColor = hasErrors ? theme.palette.error.main : theme.palette.text.border;

          return (
            <Accordion
              key={profileId}
              expanded={hasErrors || manualExpand[profileId] || false}
              onChange={() => handleAccordion(profileId, hasErrors)}
              style={{ ...NewPostStyledComponent.AccordionStyle, borderColor }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={NewPostStyledComponent.AccordionSummary}
              >
                <Grid container justifyContent="space-between">
                  <Grid alignItems="center" display="flex">
                    {isOrgConnectedProfilesData
                      ? CommonFunction.SocialMediaBadgeWithName(
                          requestData[profileId][0]?.connected_profile_details
                        )
                      : CommonFunction.AccountBadgeWithName(
                          requestData[profileId][0]?.connected_profile_details
                        )}
                  </Grid>

                  <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormGroup row>
                      {Object.values(POST_TYPES).map((type) => (
                        <FormControlLabel
                          key={type.value}
                          control={
                            <Checkbox
                              checked={checkPostTypeChecked(profileId, type.value)}
                              onChange={() => handlePostChange(profileId, type.value)}
                            />
                          }
                          label={
                            <NewPostStyledComponent.AccordianLabelStyles>
                              {type.label}
                            </NewPostStyledComponent.AccordianLabelStyles>
                          }
                        />
                      ))}
                    </FormGroup>
                  </Grid>
                </Grid>
                <Divider />
              </AccordionSummary>

              <AccordionDetails sx={{ padding: 0.5 }}>
                {requestData[profileId].map((profile) => (
                  <Box
                    key={profile.post_type}
                    style={{
                      ...NewPostStyledComponent.AccordionStyle,
                      borderColor: profile.errors?.length > 0 ? theme.palette.error.main : theme.palette.background.paper,
                      padding: '0.5rem',
                      marginBottom: '0.5rem',
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 1 }}>
                      {POST_TYPES[profile.post_type]?.label || profile.post_type}
                    </Typography>

                    <PostDescription
                      handleChangeDescription={(val) => handleChangeDescription(profileId, profile, val)}
                      valueDescription={profile.description}
                    />

                    {profile.post_type === PostUtil.PostType.SAVED_STORY && (
                      <TextField
                        fullWidth
                        label="Title (Max 45 chars)"
                        value={profile.title || ''}
                        onChange={(e) => handleChangeTitle(profileId, profile, e.target.value)}
                        sx={{ mt: 2 }}
                        inputProps={{ maxLength: 45 }}
                      />
                    )}

                    {profile.post_type === PostUtil.PostType.SPOTLIGHT && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!profile.save_to_profile}
                            onChange={(e) => handleSaveToProfile(profileId, profile, e.target.checked)}
                          />
                        }
                        label="Save to Profile"
                        sx={{ mt: 1 }}
                      />
                    )}

                    {profile.media_data?.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <ImageDraggable
                          mediaFiles={profile.media_data}
                          handleRemoveFiles={(url) => handleRemoveFiles(profileId, profile.post_type, url)}
                          handleReOrderFiles={(oldIndex, newIndex) =>
                            handleReOrderFiles(profileId, profile.post_type, oldIndex, newIndex)
                          }
                        />
                      </Box>
                    )}

                    {profile.errors?.length > 0 && errorView(profile)}
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          );
        })}
    </>
  );
});

export default memo(SnapchatConditionComponent);